import { SingleDatePicker } from "@/components/SingleDatePicker";
import { type CalendarView, useCalendarSettings } from "@repo/api";
import { Button, Text, chevronLeftIcon, chevronRightIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import { FlexDiv, LocalizedDate, LocalizedWeekday } from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { addDays, getWeek, isSameDay } from "date-fns";

import { type AppointmentFilterActions } from "./appointmentFilterReducer";

interface WeekPickerProps {
  actions: AppointmentFilterActions;
  fromDate: Date;
  calendarMode: CalendarView;
}

export const WeekPicker = ({
  actions,
  fromDate,
  calendarMode,
}: WeekPickerProps) => {
  const dayIncrease = calendarMode === "OVERVIEW" ? 7 : 1;
  const { data: calendarSettings } = useCalendarSettings();
  const weekNumber = getWeek(fromDate, {
    weekStartsOn: calendarSettings.firstDayOfWeek === "monday" ? 1 : 0,
    firstWeekContainsDate: 4,
  });

  const today = new Date();
  const isTodaySelected = isSameDay(fromDate, today);

  return (
    <FlexDiv alignItems="center">
      <Button
        text={
          dayIncrease === 7
            ? t("action.previous_week")
            : t("action.previous_day")
        }
        size="small"
        iconOnly
        ssrIcon={chevronLeftIcon}
        type="tertiary"
        onClick={() => {
          actions.setDate({ from: addDays(fromDate, -dayIncrease) });
          trackAnalytics("show_selected_previous_week");
        }}
      />
      <SingleDatePicker
        id="date"
        customInput={
          <Text textStyle="Heading.XS" noWrap>
            {calendarSettings.showWeekNumber &&
              `${t("prompt.week_number", { weekNumber })} - `}
            <LocalizedDate date={fromDate} /> (
            <LocalizedWeekday date={fromDate} />)
          </Text>
        }
        onChange={(date: Date) => {
          actions.setDate({ from: date });
          trackAnalytics("select_date");
        }}
        value={fromDate}
      />
      <Button
        text={dayIncrease === 7 ? t("action.next_week") : t("action.next_day")}
        size="small"
        iconOnly
        ssrIcon={chevronRightIcon}
        type="tertiary"
        onClick={() => {
          actions.setDate({ from: addDays(fromDate, dayIncrease) });
          trackAnalytics("show_selected_next_week");
        }}
      />
      <Button
        type={isTodaySelected ? "primary" : "secondary"}
        size="small"
        onClick={() => {
          actions.setDate({ from: today });
          trackAnalytics("calendar_today_button_clicked");
        }}
      >
        {t("label.today")}
      </Button>
    </FlexDiv>
  );
};
